import { Link } from 'react-router-dom';
import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { BsActivity, BsChevronDown, BsGear, BsGraphUpArrow, BsListCheck } from 'react-icons/bs';
import iconDashboard from "../../assets/images/icon-dashboard.svg";
import manageQuestion from "../../assets/images/Manage Questions.svg";
import manageLanguage from "../../assets/images/Manage Language.svg";
import manageSkill from "../../assets/images/Manage Skills.svg";
import manageQuestionSet from "../../assets/images/Manage Question Sets.svg";
import { useTranslation } from 'react-i18next';
import manageRole from "../../assets/images/Manage Job Role.svg";
import manageResumeAnalysis from "../../assets/images/Resume Analysis.svg";
import manageJDAnalysis from "../../assets/images/JD Analysis.svg";
import manageUser from "../../assets/images/users.svg";
import iconPlans from "../../assets/images/icon-plans.svg";
import iconSupport from "../../assets/images/help.svg";
import { TooltipCustom } from "../Common/Tooltip/Tooltip";

interface Props {
    visible: boolean,
    toggleClass?: string;
    onToggleHandler?(className: boolean): void;
}

/**
 * The configuration screens, gathered under one Settings group.
 *
 * Manage Companies and Manage Plans stay at the top level deliberately: they are
 * day-to-day operational screens, not setup, so burying them behind a collapsed
 * group would add a click to the two most-visited pages after the dashboard.
 */
const SETTINGS_ITEMS = [
    { id: "t-2", path: "/languages", icon: manageLanguage, label: "MANAGE_LANGUAGE" },
    { id: "t-99", path: "/role-list", icon: manageRole, label: "MANAGE_JOB_ROLES" },
    { id: "t-3", path: "/skills", icon: manageSkill, label: "MANAGE_SKILL" },
    { id: "t-4", path: "/questions", icon: manageQuestion, label: "MANAGE_QUESTIONS" },
    { id: "t-5", path: "/questions-sets", icon: manageQuestionSet, label: "MANAGE_QUESTION_SETS" },
    { id: "t-7", path: "/resume-analysis", icon: manageResumeAnalysis, label: "MANAGE_RESUME_ANALYSIS" },
    { id: "t-8", path: "/jd-analysis", icon: manageJDAnalysis, label: "MANAGE_JD_ANALYSIS" },
    { id: "t-9", path: "/answer-rating-analysis", icon: manageJDAnalysis, label: "MANAGE_ANSWER_RATING_ANALAYSIS" },
    { id: "t-10", path: "/question-analysis", icon: manageJDAnalysis, label: "MANAGE_QUESTION_ANALAYSIS" },
];

/**
 * Wraps a sidebar row so its full label is still reachable when the row cannot
 * show it: the longer Settings entries are clipped to an ellipsis at the
 * sidebar's fixed width, and below 1024px (or when the menu is minimised) the
 * label is hidden altogether and only the icon remains.
 *
 * The tooltip is attached only when the text is actually cut off, so the wide
 * sidebar — where every label already reads in full — stays free of tooltips
 * that would repeat what is on screen.
 */
const MenuRow = (props: { id: string; label: string; children: (textRef: React.RefObject<HTMLSpanElement>) => ReactElement }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [clipped, setClipped] = useState(false);
    const row = props.children(textRef);

    useLayoutEffect(() => {
        const text = textRef.current;
        if (!text) return;

        // clientWidth 0 means the label is display:none (collapsed sidebar);
        // scrollWidth past clientWidth means the ellipsis has kicked in.
        const measure = () => setClipped(text.clientWidth === 0 || text.scrollWidth > text.clientWidth + 1);
        measure();

        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(text);
        return () => observer.disconnect();
        // `clipped` is a dependency because flipping it re-mounts the row inside
        // (or out of) the tooltip wrapper, which replaces the span the observer
        // is watching. Re-measuring the fresh node yields the same verdict, so
        // this settles after one extra pass rather than looping.
    }, [props.label, clipped]);

    if (!clipped) return row;

    return (
        <TooltipCustom
            id={`${props.id}-tooltip`}
            position="right"
            message={props.label}
            className="menu-tooltip"
            container={document.body}
            st={row}
        />
    );
};

const VerticalMenu = (props: Props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const isActive = (path: any) => location.pathname === path;
    let role = localStorage.getItem("role");
    const navigate = useNavigate();

    // A settings page is open, so the group that holds it must be open too —
    // otherwise the active item is invisible and the sidebar looks wrong.
    const settingsHasActive = SETTINGS_ITEMS.some((item) => isActive(item.path));
    const [settingsOpen, setSettingsOpen] = useState(settingsHasActive);

    useEffect(() => {
        if (settingsHasActive) setSettingsOpen(true);
    }, [settingsHasActive]);

    useEffect(() => {
        if (role === "CLIENT" || role === "LEAFG") {
            navigate('/access-denied', { replace: true });
        }
    }, [role, navigate]);

    return (
        <>
            <div id="vertical_menu" className="verticle-menu px-2">

                <div className="menu-list">
                    <MenuRow id="t-1" label={t("DASHBOARD")}>
                        {(textRef) => (
                            <Link id="t-1" to={'/dashboard'} className={"menu-item" + (isActive('/dashboard') ? " active-link" : "")}>
                                <img src={iconDashboard} alt="icon" className="menu-icon" />
                                <span className='nav-text' ref={textRef}>{t("DASHBOARD")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    {/* Directly under the dashboard: the same "what have we
                        got" question, asked of the external AI sourcing index
                        rather than the platform's own candidates. */}
                    <MenuRow id="t-14" label={t("AI_SOURCING_ANALYTICS")}>
                        {(textRef) => (
                            <Link id="t-14" to={'/ai-sourcing-analytics'} className={"menu-item" + (isActive('/ai-sourcing-analytics') ? " active-link" : "")}>
                                <span className="menu-icon menu-icon-glyph"><BsGraphUpArrow size={18} /></span>
                                <span className='nav-text' ref={textRef}>{t("AI_SOURCING_ANALYTICS")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    {/* The third analytics screen, and the only one that acts:
                        it shows AI assessments and interviews in flight and
                        carries the reset that frees a candidate stuck in one. */}
                    <MenuRow id="t-15" label={t("AI_SESSION_MONITOR")}>
                        {(textRef) => (
                            <Link id="t-15" to={'/ai-session-monitor'} className={"menu-item" + (isActive('/ai-session-monitor') ? " active-link" : "")}>
                                <span className="menu-icon menu-icon-glyph"><BsActivity size={18} /></span>
                                <span className='nav-text' ref={textRef}>{t("AI_SESSION_MONITOR")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    {/* The monitor's working list on its own — the queue an
                        admin comes to clear, without the numbers above it. */}
                    <MenuRow id="t-16" label={t("AI_SESSIONS")}>
                        {(textRef) => (
                            <Link id="t-16" to={'/ai-sessions'} className={"menu-item" + (isActive('/ai-sessions') ? " active-link" : "")}>
                                <span className="menu-icon menu-icon-glyph"><BsListCheck size={18} /></span>
                                <span className='nav-text' ref={textRef}>{t("AI_SESSIONS")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    <MenuRow id="t-6" label={t("MANAGE_COMPANIES")}>
                        {(textRef) => (
                            <Link id="t-6" to={'/companies'} className={"menu-item" + (isActive('/companies') ? " active-link" : "")}>
                                <img src={manageUser} alt="icon" className="menu-icon" />
                                <span className='nav-text' ref={textRef}>{t("MANAGE_COMPANIES")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    <MenuRow id="t-11" label={t("MANAGE_PLANS")}>
                        {(textRef) => (
                            <Link id="t-11" to={'/plans'} className={"menu-item" + (isActive('/plans') ? " active-link" : "")}>
                                <img src={iconPlans} alt="icon" className="menu-icon" />
                                <span className='nav-text' ref={textRef}>{t("MANAGE_PLANS")}</span>
                            </Link>
                        )}
                    </MenuRow>

                    <div className={"menu-group" + (settingsOpen ? " is-open" : "")}>
                        <MenuRow id="t-settings" label={t("SETTINGS")}>
                            {(textRef) => (
                                <button
                                    id="t-settings"
                                    type="button"
                                    className={"menu-item menu-group-toggle" + (!settingsOpen && settingsHasActive ? " active-link" : "")}
                                    aria-expanded={settingsOpen}
                                    aria-controls="settings-submenu"
                                    onClick={() => setSettingsOpen((open) => !open)}
                                >
                                    <span className="menu-icon menu-icon-glyph"><BsGear size={19} /></span>
                                    <span className='nav-text' ref={textRef}>{t("SETTINGS")}</span>
                                    <BsChevronDown size={11} className="menu-group-caret" aria-hidden="true" />
                                </button>
                            )}
                        </MenuRow>

                        <div className="menu-submenu" id="settings-submenu" hidden={!settingsOpen}>
                            {SETTINGS_ITEMS.map((item) => (
                                <MenuRow key={item.path} id={item.id} label={t(item.label)}>
                                    {(textRef) => (
                                        <Link
                                            id={item.id}
                                            to={item.path}
                                            className={"menu-item" + (isActive(item.path) ? " active-link" : "")}
                                        >
                                            <img src={item.icon} alt="icon" className="menu-icon" />
                                            <span className='nav-text' ref={textRef}>{t(item.label)}</span>
                                        </Link>
                                    )}
                                </MenuRow>
                            ))}
                        </div>
                    </div>

                    <MenuRow id="t-12" label={t("APP_VERSIONS")}>
                        {(textRef) => (
                            <Link id="t-12" to={'/app-versions'} className={"menu-item" + (isActive('/app-versions') ? " active-link" : "")}>
                                <img src={manageJDAnalysis} alt="icon" className="menu-icon" />
                                <span className='nav-text' ref={textRef}>{t("APP_VERSIONS")}</span>
                            </Link>
                        )}
                    </MenuRow>
                    <MenuRow id="t-13" label={t("SUPPORT")}>
                        {(textRef) => (
                            <Link id="t-13" to={'/client-portal-support'} className={"menu-item" + (isActive('/client-portal-support') ? " active-link" : "")}>
                                <img src={iconSupport} alt="icon" className="menu-icon" />
                                <span className='nav-text' ref={textRef}>{t("SUPPORT")}</span>
                            </Link>
                        )}
                    </MenuRow>
                </div>
            </div>
        </>
    )
}
export default VerticalMenu;
